import { useRef, useState, useEffect } from "react";
import Slider from "react-slick";
import { LazyLoadImage } from "react-lazy-load-image-component";
import "slick-carousel/slick/slick.css";
import "slick-carousel/slick/slick-theme.css";
import { ChevronLeft, ChevronRight } from "lucide-react";
import clsx from "clsx";
import { API_URL } from "../../config";

const ProductImageGallery = ({
  images = [],
  aspectRate = "16:9",
  productName = "",
}) => {
  const [validImages, setValidImages] = useState([]);

  const [nav1, setNav1] = useState(null);
  const [nav2, setNav2] = useState(null);
  const slider1 = useRef(null);
  const slider2 = useRef(null);

  const getImageUrl = (imagePath, width = 1024) => {
    if (!imagePath) return "/default/no-image.avif";
    if (imagePath.startsWith("/default/")) return imagePath;
    return `${API_URL}/api/v2/Imagenes?src=${encodeURIComponent(imagePath)}&w=${width}&fmt=webp&q=80`;
  };

  useEffect(() => {
    let cancelled = false;
    const sourceImages = Array.isArray(images)
      ? [...new Set(images.filter(Boolean))]
      : [];

    if (!sourceImages.length) {
      setValidImages(["/default/no-image.avif"]);
      return () => {
        cancelled = true;
      };
    }

    const checkImage = (imagePath) =>
      new Promise((resolve) => {
        const probe = new Image();
        probe.onload = () => resolve(imagePath);
        probe.onerror = () => resolve(null);
        probe.src = getImageUrl(imagePath, 320);
      });

    Promise.all(sourceImages.map(checkImage)).then((results) => {
      if (cancelled) return;
      const okImages = results.filter(Boolean);
      setValidImages(okImages.length ? okImages : ["/default/no-image.avif"]);
    });

    return () => {
      cancelled = true;
    };
  }, [images]);

  useEffect(() => {
    setNav1(slider1.current);
    setNav2(slider2.current);
  }, []);

  const mainSettings = {
    asNavFor: nav2,
    ref: slider1,
    slidesToShow: 1,
    swipeToSlide: true,
    arrows: true,
    fade: true,
    initialSlide: 0,
    infinite: validImages.length >= 2,
  };

  const thumbSettings = {
    asNavFor: nav1,
    ref: slider2,
    slidesToShow: validImages.length >= 3 ? 3 : validImages.length === 2 ? 2 : 1,
    swipeToSlide: true,
    focusOnSelect: true,
    centerPadding: "4px",
    arrows: true,
    dots: false,
    centerMode: false,
    initialSlide: 0,
    infinite: validImages.length >= 2,
    prevArrow: (
      <button className="custom-prev">
        <ChevronLeft size={32} color="#000" />
      </button>
    ),
    nextArrow: (
      <button className="custom-next">
        <ChevronRight size={32} color="#000" />
      </button>
    ),
  };

  return (
    <div>
      <Slider {...mainSettings}>
        {validImages.map((image, i) => (
          
          <div key={i + image} className="single-image">

            <img
              src={getImageUrl(image, 1024)}
              className={clsx(
                "img-fluid object-fit-cover",
                !aspectRate === "16:9" && "w-100"
                  ? "aspect-ratio-16/9"
                  : "aspect-ratio-4/3"
              )}
              alt={productName || "Imagen del producto"}
              onError={(e) => {
                e.target.onerror = null;
                e.target.src = "/default/no-image.avif";
              }}
              width={"480px"}
              height={"480px"}
            />
          </div>
        ))}
      </Slider>

      <div className="product-small-image-wrapper mt-15">
        {validImages.length <= 3 ? (
          <div className="tc-modal-thumbs-inline">
            {validImages.map((image, i) => (
              <button
                key={i + image}
                type="button"
                className="tc-modal-thumb-btn"
                onClick={() => slider1.current?.slickGoTo(i)}
              >
                <LazyLoadImage
                  src={getImageUrl(image, 220)}
                  className="aspect-square object-contain"
                  alt={productName || "Miniatura del producto"}
                  onError={(e) => {
                    e.target.onerror = null;
                    e.target.src = "/default/no-image.avif";
                  }}
                  width={"120px"}
                  height={"120px"}
                />
              </button>
            ))}
          </div>
        ) : (
          <Slider {...thumbSettings}>
            {validImages.map((image, i) => (
              <div
                key={i + image}
                className={clsx("flex flex-row justify-center items-center", "")}
              >
                <LazyLoadImage
                  src={getImageUrl(image, 220)}
                  className="aspect-square object-contain"
                  alt={productName || "Miniatura del producto"}
                  onError={(e) => {
                    e.target.onerror = null;
                    e.target.src = "/default/no-image.avif";
                  }}
                  width={"120px"}
                  height={"120px"}
                />
              </div>
            ))}
          </Slider>
        )}
      </div>
    </div>
  );
};

export default ProductImageGallery;
